import { SUBSCRIPTION_ACTIONS } from "../actions/subscriptionActions";

const initialState = {
  // Package selection
  selectedPackage: null,
  userCount: 1,
  billingType: "monthly",

  // Discount codes
  discountCode: null,
  appliedDiscount: null,
  isValidatingDiscount: false,
  discountError: null,

  // Pricing calculations
  calculatedPricing: {
    subtotal: 0,
    discount: 0,
    total: 0,
    basePrice: 0,
    userCount: 1,
  },

  // Registration flow
  registrationData: null,
  isRegistrationFlow: false,

  // Subscription flow state
  flowStep: "package_selection", // package_selection, configuration, registration, payment, completed
  subscriptionResult: null,

  // UI state
  isLoading: false,
  error: null,

  // My Subscription Management
  currentSubscription: null,
  isFetchingSubscription: false,
  subscriptionError: null,

  // Billing data
  billingHistory: {
    subscription: null,
    paymentHistory: [],
  },
  isFetchingBilling: false,
  billingError: null,

  // Cancellation state
  isCancelling: false,

  // Subscription status check
  isCheckingStatus: false,
  // Only trust a cached "true" (active subscription). A cached "false" is
  // dangerous — it would falsely gate the user before the fresh API call
  // returns. Start "false" cases at null so we always re-verify on load.
  hasActiveSubscription: (() => {
    try {
      if (localStorage.getItem("hasActiveSubscription") === "true") return true;
    } catch (_) {}
    return null;
  })(),
  subscriptionStatus: null,
  cancellationError: null,

  // Metadata
  lastUpdated: null,
  flowStartTime: null,
};

const subscriptionReducer = (state = initialState, action) => {
  switch (action.type) {
    // Package selection
    case SUBSCRIPTION_ACTIONS.SET_SELECTED_PACKAGE:
      return {
        ...state,
        selectedPackage: action.payload,
        lastUpdated: Date.now(),
        error: null,
      };

    case SUBSCRIPTION_ACTIONS.SET_USER_COUNT:
      return {
        ...state,
        userCount: Math.max(1, action.payload),
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.SET_BILLING_TYPE:
      return {
        ...state,
        billingType: action.payload,
        lastUpdated: Date.now(),
      };

    // Discount codes
    case SUBSCRIPTION_ACTIONS.SET_DISCOUNT_CODE:
      return {
        ...state,
        discountCode: action.payload,
        discountError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.SET_APPLIED_DISCOUNT:
      return {
        ...state,
        appliedDiscount: action.payload,
        discountError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_START:
      return {
        ...state,
        isValidatingDiscount: true,
        discountError: null,
      };

    case SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_SUCCESS:
      return {
        ...state,
        isValidatingDiscount: false,
        appliedDiscount: action.payload,
        discountError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_ERROR:
      return {
        ...state,
        isValidatingDiscount: false,
        discountError: action.payload,
        appliedDiscount: null,
      };

    case SUBSCRIPTION_ACTIONS.CLEAR_DISCOUNT:
      return {
        ...state,
        discountCode: null,
        appliedDiscount: null,
        discountError: null,
        isValidatingDiscount: false,
        lastUpdated: Date.now(),
      };

    // Pricing calculations
    case SUBSCRIPTION_ACTIONS.SET_CALCULATED_PRICING:
      return {
        ...state,
        calculatedPricing: {
          ...state.calculatedPricing,
          ...action.payload,
        },
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.CALCULATE_PRICING:
      // This action is handled by the async action creator
      return state;

    // Registration flow
    case SUBSCRIPTION_ACTIONS.SET_REGISTRATION_DATA:
      return {
        ...state,
        registrationData: action.payload,
        isRegistrationFlow: true,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.CLEAR_REGISTRATION_DATA:
      return {
        ...state,
        registrationData: null,
        isRegistrationFlow: false,
        lastUpdated: Date.now(),
      };

    // Subscription flow
    case SUBSCRIPTION_ACTIONS.START_SUBSCRIPTION_FLOW:
      return {
        ...state,
        selectedPackage: action.payload,
        flowStep: "package_selection",
        flowStartTime: Date.now(),
        subscriptionResult: null,
        error: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.COMPLETE_SUBSCRIPTION_FLOW:
      return {
        ...state,
        flowStep: "completed",
        subscriptionResult: action.payload,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.CLEAR_SUBSCRIPTION_DATA:
      return {
        ...initialState,
        lastUpdated: Date.now(),
      };

    // UI state
    case SUBSCRIPTION_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case SUBSCRIPTION_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case SUBSCRIPTION_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    // My Subscription Management
    case SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_START:
      return {
        ...state,
        isFetchingSubscription: true,
        subscriptionError: null,
      };

    case SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        isFetchingSubscription: false,
        currentSubscription: action.payload,
        subscriptionError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_ERROR:
      return {
        ...state,
        isFetchingSubscription: false,
        subscriptionError: action.payload,
        currentSubscription: null,
      };

    // Subscription Cancellation
    case SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_START:
      return {
        ...state,
        isCancelling: true,
        cancellationError: null,
      };

    case SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        isCancelling: false,
        currentSubscription: action.payload, // Updated subscription after cancellation
        cancellationError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_ERROR:
      return {
        ...state,
        isCancelling: false,
        cancellationError: action.payload,
      };

    // Billing History
    case SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_START:
      return {
        ...state,
        isFetchingBilling: true,
        billingError: null,
      };

    case SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_SUCCESS:
      return {
        ...state,
        isFetchingBilling: false,
        billingHistory: action.payload,
        billingError: null,
        lastUpdated: Date.now(),
      };

    case SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_ERROR:
      return {
        ...state,
        isFetchingBilling: false,
        billingError: action.payload,
      };

    // Subscription status check cases
    case SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_START:
      return {
        ...state,
        isCheckingStatus: true,
        error: null,
      };

    case SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_SUCCESS:
      try {
        localStorage.setItem(
          "hasActiveSubscription",
          String(Boolean(action.payload.hasSubscription))
        );
      } catch (_) {}
      return {
        ...state,
        isCheckingStatus: false,
        hasActiveSubscription: action.payload.hasSubscription,
        subscriptionStatus: action.payload.status,
        currentSubscription: action.payload.subscription,
        error: null,
      };

    case SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_ERROR:
      return {
        ...state,
        isCheckingStatus: false,
        // Keep hasActiveSubscription as-is on error — don't treat a transient
        // network/auth failure as "no subscription" or it falsely gates users.
        error: action.payload,
      };

    case SUBSCRIPTION_ACTIONS.RESET_SUBSCRIPTION_STATUS:
      try {
        localStorage.removeItem("hasActiveSubscription");
      } catch (_) {}
      return {
        ...state,
        isCheckingStatus: false,
        hasActiveSubscription: null,
        subscriptionStatus: null,
        currentSubscription: null,
        error: null,
      };

    default:
      return state;
  }
};

// Selectors
export const selectSelectedPackage = (state) =>
  state.subscription.selectedPackage;
export const selectUserCount = (state) => state.subscription.userCount;
export const selectBillingType = (state) => state.subscription.billingType;
export const selectAppliedDiscount = (state) =>
  state.subscription.appliedDiscount;
export const selectCalculatedPricing = (state) =>
  state.subscription.calculatedPricing;
export const selectIsValidatingDiscount = (state) =>
  state.subscription.isValidatingDiscount;
export const selectDiscountError = (state) => state.subscription.discountError;
export const selectRegistrationData = (state) =>
  state.subscription.registrationData;
export const selectIsRegistrationFlow = (state) =>
  state.subscription.isRegistrationFlow;
export const selectFlowStep = (state) => state.subscription.flowStep;
export const selectSubscriptionError = (state) => state.subscription.error;
export const selectIsLoading = (state) => state.subscription.isLoading;

// My Subscription selectors
export const selectCurrentSubscription = (state) =>
  state.subscription.currentSubscription;
export const selectIsFetchingSubscription = (state) =>
  state.subscription.isFetchingSubscription;
export const selectSubscriptionFetchError = (state) =>
  state.subscription.subscriptionError;
export const selectIsCancelling = (state) => state.subscription.isCancelling;
export const selectCancellationError = (state) =>
  state.subscription.cancellationError;

// Subscription status selectors
export const selectIsCheckingStatus = (state) => state.subscription.isCheckingStatus;
export const selectHasActiveSubscription = (state) => state.subscription.hasActiveSubscription;
export const selectSubscriptionStatus = (state) => state.subscription.subscriptionStatus;

// Billing selectors
export const selectBillingHistory = (state) =>
  state.subscription.billingHistory;
export const selectIsFetchingBilling = (state) =>
  state.subscription.isFetchingBilling;
export const selectBillingError = (state) => state.subscription.billingError;

// Complex selectors
export const selectIsPackageConfigured = (state) => {
  const subscription = state.subscription;
  return !!(subscription.selectedPackage && subscription.userCount > 0);
};

export const selectCanProceedToPayment = (state) => {
  const subscription = state.subscription;
  return !!(
    subscription.selectedPackage &&
    subscription.userCount > 0 &&
    subscription.calculatedPricing.total >= 0 &&
    !subscription.isValidatingDiscount
  );
};

export const selectVolumeDiscount = (state) => {
  const { selectedPackage, userCount } = state.subscription;
  if (
    !selectedPackage?.volumeDiscounts ||
    selectedPackage.pricingModel !== "per_user"
  ) {
    return null;
  }

  return (
    selectedPackage.volumeDiscounts
      .filter((vd) => userCount >= vd.minUsers)
      .sort((a, b) => b.minUsers - a.minUsers)[0] || null
  );
};

export const selectTotalSavings = (state) => {
  const { calculatedPricing } = state.subscription;
  const volumeDiscount = selectVolumeDiscount(state);

  let savings = calculatedPricing.discount || 0;

  // Add volume discount savings (this is already calculated in the pricing)
  // but we can show it separately for UI purposes
  if (volumeDiscount) {
    // Volume discount is already applied in calculatedPricing.subtotal
    // This selector is for display purposes
  }

  return savings;
};

export const selectSubscriptionSummary = (state) => {
  const subscription = state.subscription;
  return {
    package: subscription.selectedPackage,
    userCount: subscription.userCount,
    billingType: subscription.billingType,
    pricing: subscription.calculatedPricing,
    discount: subscription.appliedDiscount,
    volumeDiscount: selectVolumeDiscount(state),
    canProceed: selectCanProceedToPayment(state),
    isConfigured: selectIsPackageConfigured(state),
  };
};

export default subscriptionReducer;
